import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PortalClass } from '../types';
import { portalApi } from '../utils/api';
import { usePortalAuth } from '../context/PortalAuthContext';
import AddClassDialog, { type AddClassPayload } from '../components/AddClassDialog';

type FacultyPortalPageProps = {
  educatorUserIdOverride?: string;
};

function classPrimaryText(item: PortalClass) {
  return item.class_name || `${item.course_code} ${item.section_code}`;
}

function classSecondaryText(item: PortalClass) {
  return `${item.course_code} ${item.section_code} • ${item.term}`;
}

function buildEducatorQuerySuffix(educatorUserId?: string) {
  return educatorUserId
    ? `?educator_user_id=${encodeURIComponent(educatorUserId)}`
    : '';
}

function ClassList({
  items,
  onSelect,
  emptyMessage,
}: {
  items: PortalClass[];
  onSelect: (sectionId: string) => void;
  emptyMessage: string;
}) {
  if (items.length === 0) {
    return (
      <Box sx={{ px: 3, pb: 3 }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {items.map((item, index) => (
        <ListItemButton
          key={item.section_id}
          divider={index < items.length - 1}
          onClick={() => onSelect(item.section_id)}
          sx={{ px: 3 }}
        >
          <ListItemText
            primary={classPrimaryText(item)}
            secondary={classSecondaryText(item)}
          />
        </ListItemButton>
      ))}
    </List>
  );
}

function FacultyPortalPage({ educatorUserIdOverride }: FacultyPortalPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = usePortalAuth();
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const educatorUserId = educatorUserIdOverride || searchParams.get('educator_user_id') || undefined;
  const adminViewingEducator = user?.role === 'admin' && Boolean(educatorUserId);
  const educatorQuerySuffix = buildEducatorQuerySuffix(educatorUserId);

  const { data: currentClasses = [] } = useQuery<PortalClass[]>({
    queryKey: ['portal', 'faculty', 'current-classes', educatorUserId],
    queryFn: () => portalApi.getFacultyCurrentClasses(educatorUserId),
  });
  const { data: pastClasses = [] } = useQuery<PortalClass[]>({
    queryKey: ['portal', 'faculty', 'past-classes', educatorUserId],
    queryFn: () => portalApi.getFacultyPastClasses(educatorUserId),
  });

  const createMutation = useMutation({
    mutationFn: (payload: AddClassPayload) => {
      const requestPayload = adminViewingEducator && educatorUserId
        ? { ...payload, educator_user_id: educatorUserId }
        : payload;
      return portalApi.createFacultyClass(requestPayload);
    },
    onSuccess: () => {
      setCreateDialogOpen(false);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['portal', 'faculty'] });
    },
    onError: () => setError('Unable to create class'),
  });

  const handleCreateClass = (payload: AddClassPayload) => {
    setError(null);
    createMutation.mutate(payload);
  };

  const handleSelectClass = (sectionId: string) => {
    navigate(`/educator-portal/classes/${sectionId}${educatorQuerySuffix}`);
  };

  return (
    <Box sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography variant="h4" fontWeight={700}>Educator Portal</Typography>
          <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
            Add Class
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ px: 3, pt: 3, pb: currentClasses.length > 0 ? 1 : 3 }}>
              <Typography variant="h6">Current Classes</Typography>
            </Box>
            <ClassList
              items={currentClasses}
              onSelect={handleSelectClass}
              emptyMessage="No current classes yet."
            />
          </CardContent>
        </Card>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Past Classes</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <ClassList
              items={pastClasses}
              onSelect={handleSelectClass}
              emptyMessage="No past classes yet."
            />
          </AccordionDetails>
        </Accordion>
      </Stack>

      <AddClassDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateClass}
        isSubmitting={createMutation.isPending}
        title={adminViewingEducator ? 'Create Class for Selected Educator' : 'Create Class'}
        submitLabel="Create Class"
      />
    </Box>
  );
}

export default FacultyPortalPage;
